import {
  CircularProgress,
  Menu,
  MenuItem,
  Button as SButton,
} from '@atgfr8/atg-ui-tailwind';
import { MenuDownIcon } from '@atgfr8/icons';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { createSignal, type JSX, mergeProps, splitProps } from 'solid-js';

type Props = {
  variant?: 'contained' | 'outlined' | 'text' | string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  sx?: JSX.CSSProperties;
  label: string | JSX.Element;
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  onClick?: () => void;
  disabled?: boolean;
  href?: string;
  dropdownItems?: string[]; // List of items for the dropdown
  onDropdownItemClick?: (item: string) => void; // Callback for dropdown item click
  type?: 'button' | 'submit' | 'reset';
  isLoading?: boolean;
  class?: string;
  title?: string;
  disableRipple?: boolean;
  // disableElevation?: boolean;
  color?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning'
    | undefined;
  dataTestId: string;
  saved?: boolean;
};

function Button(props: Readonly<Props>) {
  props = mergeProps(
    {
      sx: { borderRadius: '4px' },
    },
    props,
  );
  const [load, rest] = splitProps(props, [
    'variant',
    'size',
    'sx',
    'label',
    'startIcon',
    'endIcon',
    'onClick',
    'disabled',
    'href',
    'dropdownItems',
    'onDropdownItemClick',
    'type',
    'isLoading',
    'class',
    'title',
    'disableRipple',
    'saved',
    // 'disableElevation',
  ]);

  const saveConfirmationEnabled = isFeatureFlagEnabled(
    FeatureFlags.SaveButtonConfirmation,
  );
  const showSavedState = () => Boolean(load.saved) && saveConfirmationEnabled();

  // Use a callback ref to set anchorEl
  const [anchorEl, setAnchorEl] = createSignal<HTMLButtonElement>();

  const handleButtonClick = (event?: MouseEvent) => {
    if (event) {
      const target = event.currentTarget as HTMLButtonElement;
      setAnchorEl(target);
    }
    load.onClick && typeof load.onClick === 'function' && load.onClick();
  };

  const handleDropdownItemClick = (item: string) => {
    load.onDropdownItemClick &&
      typeof load.onDropdownItemClick === 'function' &&
      load.onDropdownItemClick(item);
  };

  return (
    <>
      <SButton
        disableRipple={load.disableRipple ?? false}
        // disableElevation={load.disableElevation ?? false}
        {...rest}
        dataTestId={rest.dataTestId}
        ref={setAnchorEl}
        color={props.color}
        variant={
          (['contained', 'outlined', 'text'].includes(load.variant ?? '')
            ? load.variant
            : 'contained') as 'contained' | 'outlined' | 'text'
        }
        class={`${load.class ?? ''} transition-colors duration-500 ${
          showSavedState()
            ? 'border-announcement-feature! bg-announcement-feature! text-white! hover:bg-announcement-feature!'
            : ''
        }`}
        type={
          load.type && ['button', 'submit', 'reset'].includes(load.type)
            ? load.type
            : 'button'
        }
        title={load.title}
        on:click={handleButtonClick}
        disabled={load.disabled}
        href={load.href}
        startIcon={
          props.isLoading ? (
            <CircularProgress color="inherit" size={20} />
          ) : (
            load.startIcon
          )
        }
        endIcon={load.endIcon ?? (load.dropdownItems ? <MenuDownIcon /> : null)}
        size={load.size}
        sx={load.sx}
      >
        {/* TODO: Do we want this or the startIcon approach? */}
        {/* <Show when={props.isLoading}>
          <CircularProgress color="inherit" size={20} />
        </Show> */}
        <div>{showSavedState() ? 'Saved' : load.label}</div>
      </SButton>
      {load.dropdownItems && (
        <Menu anchorEl={anchorEl}>
          {load.dropdownItems.map((item) => (
            <MenuItem
              value={item}
              onClick={() => handleDropdownItemClick(item)}
            >
              {item}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}

export default Button;
